import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, withLatestFrom } from 'rxjs/operators';

import { PlautoService } from '@amfam/policy/data-access';
import { AutoPolicy, Coverage, Discount, Vehicle } from '@amfam/policy/models';
import { TermPipe } from '@amfam/policy/pipes';
import { VehicleQuoteService } from '@amfam/policy/shared';
import {
  IArticle,
  IQuotes,
  ISection,
  ISectionContentRef,
  PaymentScheduleItem,
  QuoteSummaryModel
} from '@amfam/shared/models';
import { CopyService } from '@amfam/shared/utility/shared-services';

import { QuickQuoteFeatureFacade } from '../+state/quick-quote-feature/quick-quote-feature.facade';

@Injectable({ providedIn: 'root' })
export class ReviewQuoteContainerService {
  constructor(
    private quickQuoteFeatureFacade: QuickQuoteFeatureFacade,
    private copyService: CopyService,
    protected autoPolicyService: PlautoService,
    private vehicleQuoteService: VehicleQuoteService
  ) {}

  newVehicle$ = this.quickQuoteFeatureFacade.newVehicle$.pipe(
    map(vehicle => this.tranformNewVehicleInfo(vehicle))
  );
  replacedVehicle$ = this.quickQuoteFeatureFacade.vehicleToReplace$.pipe(
    map(vehicle => this.tranformReplaceVehicleInfo(vehicle))
  );
  remainingVehicles$ = this.quickQuoteFeatureFacade.remainingVehicles$.pipe(
    map(vehicles => this.tranformRemainingVehiclesInfo(vehicles))
  );

  quotes: IQuotes;

  policyCoverages$: Observable<ISection> = this.quickQuoteFeatureFacade.selectedPolicy$.pipe(
    map(policy => this.autoPolicyService.getPolicyLevelCoverages(policy)),
    map(coverages =>
      coverages
        //.filter(c => this.showCoverage(c))
        .map(coverage => ({
          option: coverage.description,
          value: this.getLimits(coverage)
        }))
    ),
    map(coverages => this.transformPolicyCoverages(coverages))
  );

  vehicleCoverages$: Observable<ISection> = this.quickQuoteFeatureFacade.selectedPolicy$.pipe(
    withLatestFrom(
      this.quickQuoteFeatureFacade.selectedVehicle$,
      this.quickQuoteFeatureFacade.newRiskDescription$
    ),
    map(([policy, vehicle, newVehicle]) =>
      this.transformVehicleCoverages(policy, vehicle, newVehicle)
    )
  );

  discounts$: Observable<ISection> = this.quickQuoteFeatureFacade.newVehicleDiscounts$.pipe(
    map(discounts =>
      discounts.map(discount => ({
        option:
          discount.name.replace(/Discount/, '') +
          (this.isAntiTheftDiscountCode(discount) ? '*' : ''),
        value: 'Applied'
      }))
    ),
    map(discounts => this.transformVehicleDiscounts(discounts))
  );

  primaryButton$: Observable<string> = this.quickQuoteFeatureFacade.futurePayments$.pipe(
    map(payments => (payments && payments.length > 0 ? 'Continue' : 'Replace Vehicle'))
  );

  private getLimits(coverage: Coverage) {
    const isImpliedCoverage = coverage.coverageType === 'Implied';
    const isLiabilityCoverage = coverage.coverageType === 'Liability Group';
    const isStandardCoverage = !isImpliedCoverage && !isLiabilityCoverage;

    let limit = '';
    for (const term of coverage.limitTerms) {
      limit = (limit ? `${limit}, ` : limit) + new TermPipe().transform(term);
    }
    // TODO: rv_premium : logic ??
    if (isStandardCoverage && !(isImpliedCoverage || isLiabilityCoverage)) {
      for (const term of coverage.deductibleTerms) {
        limit = (limit ? `${limit}, ` : limit) + new TermPipe().transform(term);
      }
    }

    return limit;
  }

  private showCoverage(coverage: Coverage) {
    const isImpliedCoverage = coverage.coverageType === 'Implied';
    const isLiabilityCoverage = coverage.coverageType === 'Liability Group';
    const isStandardCoverage = !isImpliedCoverage && !isLiabilityCoverage;
    return (
      ((!!coverage?.limitTerms[0]?.value || !!coverage?.deductibleTerms[0]?.value) &&
        isStandardCoverage) ||
      coverage.limitTerms.length > 0
    );
  }

  transformPremium(premiumDetails) {
    return this.vehicleQuoteService.mapQuotes(
      +premiumDetails?.current?.monthlyCost,
      +premiumDetails?.current?.fullCost,
      +premiumDetails?.new?.monthlyCost,
      +premiumDetails?.new?.fullCost,
      premiumDetails?.new?.policyTermMonths,
      'REPLACE'
    );
  }

  private tranformNewVehicleInfo(vehicle): IArticle {
    return {
      headerContentRef: 'policy.premiumBilling.vehicleAddedHeader',
      content: `${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''}`,
      contentIconPath: this.copyService.getCopy('policy.premiumBilling.addIcon')
    };
  }

  private tranformReplaceVehicleInfo(vehicle): IArticle {
    return {
      headerContentRef: 'policy.premiumBilling.vehiclesReplacedHeader',
      content: `${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''}`,
      contentIconPath: this.copyService.getCopy('policy.premiumBilling.replaceIcon')
    };
  }

  private tranformRemainingVehiclesInfo(vehicles: string[]): IArticle {
    return {
      headerContentRef:
        vehicles.length > 1
          ? 'policy.premiumBilling.vehiclesLeftHeader'
          : 'policy.premiumBilling.vehicleLeftHeader',
      content: vehicles.join(', ')
    };
  }

  private transformPolicyCoverages(coverages: QuoteSummaryModel[]): ISection {
    return {
      headerContentRef: <ISectionContentRef>{
        path: 'policy.premiumBilling.policyCoveragesHeader'
      },
      items: coverages.map(coverage => ({
        ...coverage,
        value: coverage.value.replace(/Per /gi, '/ ')
      })),
      iconClass: 'icon-claims'
    };
  }

  private transformVehicleCoverages(
    policy: AutoPolicy,
    vehicle: Vehicle,
    newVehicle: string
  ): ISection {
    const coverages: Coverage[] = this.autoPolicyService.getRiskLevelCoverages(policy, vehicle);
    const items: QuoteSummaryModel[] = [].concat.apply(
      [],
      coverages.map(c => this.getCoveragesInfo(c)) as QuoteSummaryModel[][]
    );

    return {
      headerContentRef: <ISectionContentRef>{
        path: 'policy.premiumBilling.vehicleCoveragesHeader',
        options: { newVehicle: newVehicle }
      },
      items,
      iconClass: 'icon-auto'
    };
  }

  private transformVehicleDiscounts(discounts: QuoteSummaryModel[]): ISection {
    return {
      headerContentRef: <ISectionContentRef>{
        path: 'policy.premiumBilling.discountsHeader'
      },
      items: discounts,
      iconClass: 'icon-complete-circle'
    };
  }

  private isAntiTheftDiscountCode(discount: Discount) {
    return discount.code === 'PAAntiTheftDisc_af';
  }

  private getCoveragesInfo(c: Coverage): QuoteSummaryModel[] {
    const limits = this.getLimitsInfo(c);
    const deductibles = this.getDeductiblesInfo(c);
    const otherCoverages = this.getOtherCoverages(c);
    return [...deductibles, limits, otherCoverages].filter(Boolean);
  }

  private getOtherCoverages(coverage: Coverage): QuoteSummaryModel {
    if (
      coverage &&
      (!coverage.limitTerms || coverage.limitTerms.length === 0) &&
      (!coverage.deductibleTerms || coverage.deductibleTerms.length === 0)
    )
      return {
        option: coverage.description,
        value: 'Included'
      };
  }

  private getLimitsInfo(coverage: Coverage): QuoteSummaryModel {
    const dollarUS = Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    });
    if (coverage.limitTerms && coverage.limitTerms.length > 0)
      return {
        option: coverage.description,
        value: coverage.limitTerms
          .map(l => {
            const term = new TermPipe().transform(l, true);
            return `${term.value} ${term.description}`;
          })
          .join(', ')
      };
    return null;
  }

  private getDeductiblesInfo(coverage: Coverage): QuoteSummaryModel[] {
    const dollarUS = Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    });
    return coverage.deductibleTerms.map(term => ({
      option: `${coverage.description} ${term.description}`,
      value: dollarUS.format(+term.value)
    }));
  }

  transformKeyValuPairs(prefix: string, data: QuoteSummaryModel[], hideValue = false) {
    const dataMap = new Map<string, string>();
    for (let i = 0; i < data.length; i++) {
      dataMap.set(`${prefix}${i + 1}`, data[i].option || '');
      if (!hideValue) dataMap.set(`${prefix}${i + 1}Value`, data[i].value || '');
    }
    return dataMap;
  }

  mapFuturePayments(payments: PaymentScheduleItem[]): QuoteSummaryModel[] {
    return this.vehicleQuoteService.mapPayments(payments);
  }
}
